import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucidePlus,
  lucideSearch,
  lucideX,
  lucideDownload,
  lucideEye,
  lucideFilePlus,
  lucideTrash2,
  lucideInbox,
  lucideChevronDown,
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmInput } from '@spartan-ng/helm/input';

import { PageHeaderComponent, StatusBadgeComponent } from '../../../shared/ui/components';

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

interface Inquiry {
  id: number;
  inquiryNumber: string;
  createdDate: Date;
  company: 'HT' | 'ATR';
  clientName: string;
  clientType: 'In-House' | 'Agent' | 'Sales' | 'New';
  services: string[];
  origin: string;
  destination: string;
  mode: 'Air' | 'Sea FCL' | 'Sea LCL' | 'Break Bulk';
  status: 'New' | 'In Progress' | 'Quoted' | 'Proceed' | 'Rejected';
}

@Component({
  selector: 'app-inquiry-list',
  standalone: true,
  // Default CD kept intentionally: component uses plain mutable arrays + ngModel two-way binding.
  // OnPush would require signals/markForCheck for every ngModel update.
  imports: [
    DatePipe,
    RouterModule,
    FormsModule,
    NgIcon,
    HlmIcon,
    HlmButton,
    HlmBadge,
    HlmInput,
    PageHeaderComponent,
    StatusBadgeComponent,
  ],
  providers: [
    DatePipe,
    provideIcons({
      lucidePlus,
      lucideSearch,
      lucideX,
      lucideDownload,
      lucideEye,
      lucideFilePlus,
      lucideTrash2,
      lucideInbox,
      lucideChevronDown,
    }),
  ],
  templateUrl: './inquiry-list.html',
  styleUrl: './inquiry-list.css',
})
export class InquiryListComponent implements OnInit {
  inquiries: Inquiry[] = [];
  filteredInquiries: Inquiry[] = [];

  readonly selectClass = SELECT_CLASS;

  // Filter properties
  searchTerm = '';
  selectedStatus = '';
  selectedCompany = '';
  selectedClientType = '';
  dateFrom = '';

  ngOnInit(): void {
    this.loadInquiries();
  }

  loadInquiries(): void {
    // Sample data — will be replaced with API call
    this.inquiries = [
      {
        id: 1,
        inquiryNumber: 'HT-INQ-2025-00001',
        createdDate: new Date('2025-01-15'),
        company: 'HT',
        clientName: 'ABC Trading Co.',
        clientType: 'In-House',
        services: ['Forwarding', 'Brokerage'],
        origin: 'SHA',
        destination: 'MNL',
        mode: 'Sea FCL',
        status: 'New',
      },
      {
        id: 2,
        inquiryNumber: 'ATR-INQ-2025-00002',
        createdDate: new Date('2025-01-16'),
        company: 'ATR',
        clientName: 'Global Freight Ltd.',
        clientType: 'Agent',
        services: ['Forwarding'],
        origin: 'HKG',
        destination: 'CEB',
        mode: 'Air',
        status: 'In Progress',
      },
      {
        id: 3,
        inquiryNumber: 'HT-INQ-2025-00003',
        createdDate: new Date('2025-01-17'),
        company: 'HT',
        clientName: 'Express Logistics',
        clientType: 'Sales',
        services: ['Forwarding', 'Trucking'],
        origin: 'SGN',
        destination: 'MNL',
        mode: 'Sea LCL',
        status: 'Quoted',
      },
      {
        id: 4,
        inquiryNumber: 'ATR-INQ-2025-00004',
        createdDate: new Date('2025-01-18'),
        company: 'ATR',
        clientName: 'Metro Imports',
        clientType: 'New',
        services: ['Forwarding', 'Brokerage', 'Trucking'],
        origin: 'TYO',
        destination: 'DVO',
        mode: 'Sea FCL',
        status: 'Proceed',
      },
      {
        id: 5,
        inquiryNumber: 'HT-INQ-2025-00005',
        createdDate: new Date('2025-01-19'),
        company: 'HT',
        clientName: 'Pacific Trade',
        clientType: 'In-House',
        services: ['Forwarding', 'AMO Accreditation'],
        origin: 'LAX',
        destination: 'MNL',
        mode: 'Air',
        status: 'New',
      },
    ];

    this.filteredInquiries = [...this.inquiries];
  }

  filterInquiries(): void {
    this.filteredInquiries = this.inquiries.filter((inquiry) => {
      if (
        this.searchTerm &&
        !inquiry.inquiryNumber.toLowerCase().includes(this.searchTerm.toLowerCase()) &&
        !inquiry.clientName.toLowerCase().includes(this.searchTerm.toLowerCase())
      ) {
        return false;
      }
      if (this.selectedStatus && inquiry.status !== this.selectedStatus) return false;
      if (this.selectedCompany && inquiry.company !== this.selectedCompany) return false;
      if (this.selectedClientType && inquiry.clientType !== this.selectedClientType) return false;
      if (this.dateFrom) {
        const filterDate = new Date(this.dateFrom);
        if (inquiry.createdDate < filterDate) return false;
      }
      return true;
    });
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.selectedCompany = '';
    this.selectedClientType = '';
    this.dateFrom = '';
    this.filteredInquiries = [...this.inquiries];
  }

  getStatusVariant(status: Inquiry['status']): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default' {
    const map: Record<Inquiry['status'], 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default'> = {
      'New': 'default',
      'In Progress': 'info',
      'Quoted': 'success',
      'Proceed': 'success',
      'Rejected': 'danger',
    };
    return map[status] ?? 'neutral';
  }

  createQuote(inquiry: Inquiry): void {
    // TODO: Navigate to quote creation page with inquiry context
    void inquiry;
  }

  deleteInquiry(inquiry: Inquiry): void {
    if (confirm(`Are you sure you want to delete inquiry ${inquiry.inquiryNumber}?`)) {
      const index = this.inquiries.findIndex((i) => i.id === inquiry.id);
      if (index > -1) {
        this.inquiries.splice(index, 1);
        this.filterInquiries();
      }
    }
  }

  exportToExcel(): void {
    // TODO: Implement Excel export functionality
  }
}
